import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Edit3, FileText, Globe, MapPin, Mail, Phone, Send, Upload } from "lucide-react";

import { socials } from "../constants/socials";
import { salaryFields } from "../constants/contact";
import { STAT_ACCENTS } from "../../dashboard/common";
import { CandidateService } from "@/services/candidateService";

// CV Formatting is disabled — commented out, not deleted. See PLAN doc
// "resilient-stirring-tulip".
// import ResumeTemplateModal from "./ResumeTemplateModal";
import ApplyToJobModal from "./ApplyToJobModal";
import CandidateProfilePanelSkeleton from "./CandidateProfilePanelSkeleton";
import SocialRow from "./SocialRow";
import { toHref, stripProtocol } from "./socialLinkHelpers";

import { Button } from "@/components/ui/Button";
import Select from "@/components/ui/Select";
import { useAuth } from "@/hooks/useAuth";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { getUserPermissions, hasAnyPermission, hasPermission } from "@/permissions/rbac";
import { PERMISSIONS } from "@/permissions/constant";
import { MODULES } from "@/permissions/modules";
import toast from "react-hot-toast";

// ─── Main Component ───────────────────────────────────────────────────────────

const CandidateProfilePanel = ({
  isLoading = false,
  candidateDetails,
  salaryDetails,
  socialData,
  candidateId,
  applicationId,
  jobId,
  panelSlot,
  summary: summaryProp,
  // The confirmed resume's `personal_information.website` — what
  // confirm-details, Edit Details and apply-to-job collect.
  website: websiteProp,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  // CV Formatting is disabled — commented out, not deleted.
  // const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);

  const resolvedJobId = jobId ?? candidateDetails?.job_id ?? null;

  const handleDownload = useCallback(async () => {
    if (!candidateId || !applicationId) return;

    try {
      await CandidateService.downloadCandidateResume(candidateId, applicationId);
    } catch (error) {
      console.error("downloadResume error:", error);
      toast.error("Failed to download resume.");
    }
  }, [candidateId, applicationId]);

  const handleEditDetails = useCallback(() => {
    navigate(`/admin/edit-candidate-profile/${candidateId}?job_id=${encodeURIComponent(resolvedJobId ?? "")}&application_id=${encodeURIComponent(applicationId || "")}`, {
      state: {
        jobTitle: candidateDetails?.job_title,
        salaryDetails,
        jobId: resolvedJobId,
        applicationId,
      },
    });
  }, [navigate, candidateId, resolvedJobId, applicationId, candidateDetails?.job_title, salaryDetails]);

  const handleReuploadResume = useCallback(() => {
    navigate(`/admin/candidate-analysis/${candidateId}/reupload-resume`, {
      state: {
        jobTitle: candidateDetails?.job_title,
        candidateName: candidateDetails?.candidate_name,
        jobId: resolvedJobId,
        applicationId,
      },
    });
  }, [navigate, candidateId, resolvedJobId, applicationId, candidateDetails?.job_title, candidateDetails?.candidate_name]);

  // `Select` has no per-option gate, so each row is decided imperatively and
  // simply left out of `options` when denied — the same effect
  // `PermissionGuard`/`ModuleGate` had, just computed before render instead
  // of wrapping JSX. `resumeTemplateAccess.canUse` (not `isAvailable` alone)
  // matches what `useModuleAccess` documents for an action control: hidden
  // both when never entitled and when temporarily down fleet-wide. The one
  // real behaviour change from `ModuleGate`'s default: a temporarily-down
  // module now disappears from the list instead of staying visible, greyed
  // out, with a tooltip explaining why.
  const userPermissions = getUserPermissions(user);
  // CV Formatting is disabled — commented out, not deleted.
  // const resumeTemplateAccess = useModuleAccess(MODULES.RESUME_TEMPLATE);
  // Apply from Suitable Jobs is the job-fit module's action — every route it
  // leads to requires the module server-side, so a tenant without it used to
  // reach the review screen and collect a 403 at the submit.
  const jobFitAccess = useModuleAccess(MODULES.AI_CANDIDATE_JOB_FIT);
  // CV Formatting is disabled — commented out, not deleted.
  // const canExportCv = hasPermission(PERMISSIONS.RESUME_TEMPLATE_EXPORT, userPermissions) && resumeTemplateAccess.canUse;
  const canManageResume = hasAnyPermission([PERMISSIONS.CANDIDATE_UPDATE, PERMISSIONS.INTERVIEW_MANAGE], userPermissions);
  const canApply = hasPermission(PERMISSIONS.CANDIDATE_UPDATE, userPermissions) && jobFitAccess.canUse;

  const actionOptions = useMemo(
    () => [
      { value: "download", label: "Download Resume", icon: Download },
      // CV Formatting is disabled — commented out, not deleted.
      // ...(canExportCv ? [{ value: "export", label: "Export Formatted CV", icon: FileText }] : []),
      ...(canManageResume ? [{ value: "reupload", label: "Reupload Resume", icon: Upload }] : []),
      ...(canManageResume ? [{ value: "edit", label: "Edit Details", icon: Edit3 }] : []),
      ...(canApply ? [{ value: "apply", label: "Apply to Job", icon: Send }] : []),
    ],
    [canManageResume, canApply],
  );

  const handleActionSelect = useCallback(
    (value) => {
      if (value === "download") handleDownload();
      // CV Formatting is disabled — commented out, not deleted.
      // else if (value === "export") setTemplateModalOpen(true);
      else if (value === "reupload") handleReuploadResume();
      else if (value === "edit") handleEditDetails();
      else if (value === "apply") setApplyModalOpen(true);
    },
    [handleDownload, handleReuploadResume, handleEditDetails],
  );

  /*
   * IMPORTANT:
   * Keep conditional returns AFTER every hook in this component.
   */
  if (isLoading) {
    return <CandidateProfilePanelSkeleton />;
  }

  const name = candidateDetails?.candidate_name || "—";

  const jobTitle = candidateDetails?.job_title || "—";

  const location = candidateDetails?.location || null;

  const email = candidateDetails?.email || null;

  const phone = candidateDetails?.phone || null;

  const website = (websiteProp || "").trim() || null;

  const summary = candidateDetails?.summary || summaryProp || null;

  const contactSocials = Object.keys(socials)
    .map((id) =>
      candidateDetails?.[id]
        ? {
            id,
            url: candidateDetails[id],
          }
        : null,
    )
    .filter(Boolean);

  const cur = salaryDetails?.current_salary ?? null;

  const exp = salaryDetails?.expected_salary ?? null;

  const getHikeValue = (expected, current, hike) => {
    if (expected > current) {
      return `+${hike}%`;
    }

    if (expected < current) {
      return `${hike}%`;
    }

    return "0%";
  };

  const getHikeAccent = (expected, current) => {
    if (expected > current) {
      return "positive";
    }

    if (expected < current) {
      return "negative";
    }

    return null;
  };

  const buildStat = (field, salaryDetailsValue, current, expected, currencySign) => {
    if (field.key === "hike") {
      if (!current || !expected || current <= 0) {
        return null;
      }

      const hike = (((expected - current) / current) * 100).toFixed(1);

      return {
        label: field.label,
        value: getHikeValue(expected, current, hike),
        accent: getHikeAccent(expected, current),
        Icon: field.Icon,
      };
    }

    const rawValue = salaryDetailsValue?.[field.key];

    if (rawValue == null) {
      return null;
    }

    return {
      label: field.label,
      value: typeof field.format === "function" ? field.format(rawValue, currencySign) : rawValue,
      accent: null,
      Icon: field.Icon,
    };
  };

  const currencySign = salaryDetails?.currency_sign || "";

  const stats = salaryFields.map((field) => buildStat(field, salaryDetails, cur, exp, currencySign)).filter(Boolean);

  const hasContact = email || phone || website || contactSocials.length > 0;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="relative shrink-0 border-b border-border-primary bg-background-secondary px-5 pb-6 pt-6 text-center">
        <h2 className="text-[24px] font-semibold leading-snug text-button-secondary">{name}</h2>

        <p className="mt-0.5 text-sm text-text-primary">
          Applying for{" "}
          {resolvedJobId ? (
            <Link to={`/admin/job-detail/${resolvedJobId}`} className="font-medium text-text-blue hover:underline">
              {jobTitle}
            </Link>
          ) : (
            jobTitle
          )}
        </p>

        {location && (
          <p className="mt-2 flex items-center justify-center gap-1.5 text-[12px] text-text-faint">
            <MapPin className="h-3 w-3 shrink-0 text-text-faint" aria-hidden="true" />
            {location}
          </p>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {/* Contact */}
        {hasContact && (
          <>
            <div className="shrink-0 px-3 pb-3 pt-4">
              <p className="mb-2.5 text-[14px] font-semibold text-text-faint">Contact</p>

              <div className="flex flex-col gap-0.5">
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className="group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[14px] text-text-primary transition-colors hover:bg-background-tertiary"
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0 text-text-faint transition-colors group-hover:text-text-primary" aria-hidden="true" />

                    <span title={email} className="min-w-0 truncate">
                      {email}
                    </span>
                  </a>
                )}

                {phone && (
                  <a
                    href={`tel:${phone}`}
                    className="group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[14px] text-text-primary transition-colors hover:bg-background-tertiary"
                  >
                    <Phone className="h-3.5 w-3.5 shrink-0 text-text-faint transition-colors group-hover:text-text-primary" aria-hidden="true" />

                    <span>{phone}</span>
                  </a>
                )}

                {website && (
                  <a
                    href={toHref(website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[14px] text-text-primary transition-colors hover:bg-background-tertiary"
                  >
                    <Globe className="h-3.5 w-3.5 shrink-0 text-text-faint transition-colors group-hover:text-text-primary" aria-hidden="true" />

                    <span title={website} className="min-w-0 truncate">
                      {stripProtocol(website)}
                    </span>
                  </a>
                )}

                {contactSocials.map(({ id, url }) => {
                  const config = socials[id];

                  if (!config) {
                    return null;
                  }

                  const Icon = config.icon;

                  return (
                    <a
                      key={id}
                      href={toHref(url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-text-muted transition-colors hover:bg-background-tertiary hover:text-text-primary"
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0 text-text-faint transition-colors group-hover:text-primary" aria-hidden="true" />

                      <span className="truncate">{stripProtocol(url)}</span>
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="mx-4 border-t border-border-primary" />
          </>
        )}

        {/* Actions — Download Resume, Export Formatted CV, Reupload Resume,
            Edit Details, Apply to Another Open Job. One consolidated menu so
            these five are reachable from every tab, not just from whichever
            tab used to own one of them.

            `value` stays pinned to "" on purpose: this is a menu of one-shot
            actions, not a persisted choice, so the trigger must keep reading
            "Actions" after a pick rather than freezing on whichever label was
            last selected. */}
        <>
          <div className="shrink-0 px-3 pb-3 pt-3">
            <Select value="" onChange={handleActionSelect} options={actionOptions} placeholder="Actions" />
          </div>

          <div className="mx-4 border-t border-border-primary" />
        </>

        {/* Social */}
        <SocialRow socialData={socialData} />

        {socialData && <div className="mx-4 border-t border-border-primary" />}

        {/* Panel Slot or Stats + Summary */}
        {panelSlot ?? (
          <>
            {stats.length > 0 && (
              <div className="shrink-0 px-2 pb-3 pt-4">
                <div className="grid grid-cols-2 gap-2">
                  {stats.map((stat, index) => {
                    const isLastOdd = stats.length % 2 !== 0 && index === stats.length - 1;

                    const accent = STAT_ACCENTS[index % STAT_ACCENTS.length];

                    return (
                      <div key={stat.label} className={`h-20 rounded-xl border p-3 ${accent.bgClass} ${accent.borderClass} ${isLastOdd ? "col-span-2" : ""}`}>
                        <p
                          className={`text-[16px] font-semibold leading-tight ${
                            stat.accent === "positive" ? "text-success" : stat.accent === "negative" ? "text-error" : accent.textClass
                          }`}
                        >
                          {stat.value}
                        </p>

                        <p className="mt-1 text-[12px] font-medium text-text-secondary">{stat.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {summary && (
              <>
                <div className="mx-4 border-t border-border-primary" />

                <div className="shrink-0 px-4 pb-5 pt-4">
                  <p className="mb-2.5 text-[14px] font-semibold text-text-faint">Summary</p>

                  <p className={`text-[11px] leading-[1.7] text-text-secondary lg:text-[13px] ${summaryExpanded ? "" : "line-clamp-4"}`}>{summary}</p>

                  {summary.length > 180 && (
                    <Button
                      onClick={() => setSummaryExpanded((prev) => !prev)}
                      className="mt-2 text-[10px] font-medium text-text-primary hover:opacity-70 lg:text-[13px]"
                    >
                      {summaryExpanded ? "Show less ↑" : "Show more ↓"}
                    </Button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* CV Formatting is disabled — commented out, not deleted. */}
      {/* <ResumeTemplateModal open={templateModalOpen} onClose={() => setTemplateModalOpen(false)} candidateId={candidateId} applicationId={applicationId} /> */}

      <ApplyToJobModal open={applyModalOpen} onClose={() => setApplyModalOpen(false)} candidateId={candidateId} />
    </div>
  );
};

export default CandidateProfilePanel;
